import { cn } from '@/lib/utils'

/**
 * Traçado do "G" da Genesis Consulting, vetorizado a partir do ficheiro oficial
 * (mestre de 264×288) e reenquadrado numa viewBox de 32×32 — a mesma caixa que o
 * prisma ocupava, para que nenhum consumidor precise de mexer em tamanhos.
 *
 * São DUAS formas e não uma: o logotipo é bicolor. O corpo do "G" é o verde
 * vivo; a cauda que fecha o círculo por baixo é o verde profundo. A costura
 * entre as duas foi levantada da versão colorida da marca, e ambas partilham o
 * mesmo referencial — desenhá-las por esta ordem reconstitui o logotipo.
 */
const MARK_BODY =
  'M13.11 0.11L17.0 0.22L20.67 1.22L22.33 2.0L24.22 3.22L26.89 5.78L28.44 8.0L29.33 9.78L30.11 12.11L30.56 15.11L30.56 17.33L30.0 20.33L29.33 19.89L28.89 19.0L28.89 18.67L28.33 17.78L27.89 16.0L26.11 12.56L25.22 10.22L24.44 9.44L23.44 9.33L23.44 7.67L10.67 7.67L10.67 13.11L17.11 13.11L17.11 16.44L16.89 17.56L16.0 19.56L14.44 21.11L13.11 21.78L11.78 22.11L9.22 22.11L7.67 21.67L6.22 20.89L4.44 19.22L2.89 16.56L2.0 13.33L1.89 8.22L2.33 5.78L4.56 3.56L6.89 2.0L10.11 0.67Z'

const MARK_TAIL =
  'M23.44 9.33L24.44 9.44L25.22 10.22L26.11 12.56L27.89 16.0L28.33 17.78L28.89 18.67L28.89 19.0L29.33 19.89L29.89 20.33L29.22 22.56L27.56 25.44L25.11 28.11L24.89 28.11L23.67 29.22L21.56 30.44L18.44 31.56L14.89 32.0L11.89 31.78L9.0 31.0L5.89 29.44L4.11 28.11L1.44 25.11L1.67 25.0L2.78 25.89L5.44 27.0L7.78 27.44L10.78 27.44L12.0 27.22L14.11 26.33L14.89 25.78L16.33 24.33L16.89 23.33L17.22 23.22L17.78 27.0L23.44 27.0Z'

export interface BrandMarkProps {
  /** Width + height in pixels. */
  size?: number
  className?: string
}

/**
 * BrandMark — o **"G" da Genesis Consulting**.
 *
 * Substituiu o Prisma (LUMEN.md §8) por decisão do owner em 2026-07-28: a
 * plataforma passa a ser assinada pela marca da casa, e não por um símbolo
 * próprio. A metáfora do prisma continua a valer como ideia de produto, mas
 * deixou de ser o que aparece no ecrã.
 *
 * Regras de uso:
 *   • As cores vêm de `--brand-mark` / `--brand-mark-deep` (globals.css). No
 *     mundo claro são os dois verdes do logotipo; no escuro colapsam no tom
 *     pálido da colourway invertida oficial. Nunca as escreva à mão aqui.
 *   • O contraste interno do "G" é NEGATIVO: o vazio da letra deixa passar o
 *     fundo. A marca precisa por isso de assentar numa superfície lisa — sobre
 *     fotografia ou gradiente forte a letra desaparece.
 *   • NUNCA aparece no relatório compartilhado (§10.5): esse documento é
 *     assinado com o logotipo COMPLETO (marca + palavra), não com o símbolo.
 */
export function BrandMark({ size = 30, className }: BrandMarkProps) {
  return (
    <svg
      role="img"
      aria-label="Genesis Consulting"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      className={cn('inline-block shrink-0 select-none', className)}
    >
      <path d={MARK_BODY} fill="var(--brand-mark)" />
      <path d={MARK_TAIL} fill="var(--brand-mark-deep)" />
    </svg>
  )
}
